import type { Disposable } from "@/shared/contracts/lifecycle";
import { HoverChargeExecutionController } from "@/features/hoverIdle/core/hoverChargeExecutionController";
import { getMessages } from "@/i18n/runtime";
import {
  createStickyTooltipController,
  type StickyTooltipController,
  type StickyTooltipSource,
} from "@/shared/ui/stickyTooltipController";
import {
  HOV_SKILL_DEFINITIONS,
  HOV_SKILL_HOLD_MS,
  HOV_SKILL_HOLD_MS_OPTIONS,
  canAffordHovSkillCost,
  getHovSkillCostShortfall,
  type HovSkillCost,
  type HovSkillDefinition,
  type HovSkillHoldMs,
  type HovSkillId,
  type HovSkillResourceBalances,
} from "./hovSkillDefinitions";
import type { HovSkillSnapshot, HovSkillSnapshotEntry } from "./hovSkillModel";
import { formatForContext } from "@/features/hoverIdle/resources/numberFormat";
import {
  formatHovSkillCoordinate,
  formatHovSkillExternalMultiplier,
  isHovSkillExternalMultiplierActive,
} from "./hovSkillFormat";
import {
  buildHovSkillTooltipSpec,
  formatHovSkillInfluenceText,
  type HovSkillTooltipContext,
  type HovSkillTooltipSpec,
} from "./hovSkillTooltipSpec";
import {
  computeHovSkillTreeLayout,
  type HovSkillTreeLayout,
} from "./hovSkillTreeLayout";
import { hugeOne, hugeZero, type HugeNumber } from "@/shared/numbers";

interface HovSkillViewOptions {
  readonly container: HTMLElement;
  readonly onAcquireRequest: (id: HovSkillId) => boolean;
  readonly holdDurationMs: HovSkillHoldMs;
  readonly onHoldDurationChange: (ms: HovSkillHoldMs) => void;
}

interface IconNodes {
  readonly definition: HovSkillDefinition;
  readonly button: HTMLButtonElement;
  readonly fill: HTMLDivElement;
  readonly label: HTMLSpanElement;
  readonly tooltip: HTMLDivElement;
  readonly tooltipName: HTMLDivElement;
  readonly tooltipDescription: HTMLDivElement;
  readonly tooltipInfluence: HTMLDivElement | null;
  readonly tooltipCost: HTMLDivElement;
  readonly tooltipState: HTMLDivElement;
  readonly externalBadge: HTMLSpanElement;
  readonly chargeController: HoverChargeExecutionController;
}

const HOV_SKILL_TOOLTIP_FLOATING_OPTIONS = {
  preferredPlacement: "bottom",
  offsetPx: 8,
} as const;

const HOV_SKILL_TOOLTIP_ACTIVE_DATASET_KEY = "hovSkillTooltipActive";

export class HovSkillView implements Disposable {
  private readonly root: HTMLDivElement;
  private readonly tree: HTMLDivElement;
  private readonly svg: SVGSVGElement;
  private readonly treeLayout: HovSkillTreeLayout;
  private readonly onAcquireRequest: (id: HovSkillId) => boolean;
  private readonly onHoldDurationChange: (ms: HovSkillHoldMs) => void;
  private readonly iconNodes = new Map<HovSkillId, IconNodes>();
  private readonly cleanups: Array<() => void> = [];
  private lastResources: HovSkillResourceBalances = { hovCore: hugeZero() };
  private lastSnapshot: HovSkillSnapshot = { acquired: new Set(), entries: [] };
  private lastAmplifierGainOHovMultiplier: HugeNumber = hugeOne();
  private lastOHovHovCoreGainMultiplier: HugeNumber = hugeOne();
  private lastOHovHovCoreGainScalingUnlocked = false;
  private lastMpCapacityCoreOHovHovCoreGainMultiplier: HugeNumber = hugeOne();
  private lastFreeMpCoreOHovHovCoreGainMultiplier: HugeNumber = hugeOne();
  private lastRaBambooMultiplier: HugeNumber = hugeOne();
  private lastRaPineMultiplier: HugeNumber = hugeOne();
  private lastApDeerExponentBonus = 0;
  private lastApButterflyExponentBonus = 0;
  private showLockedContent = false;
  private holdMs: HovSkillHoldMs = HOV_SKILL_HOLD_MS;
  private readonly holdDurationButtons: HTMLButtonElement[] = [];
  private readonly tooltipController: StickyTooltipController =
    createStickyTooltipController();
  private activeTooltipReference: HTMLButtonElement | null = null;

  private readonly handleRootPointerDown = (event: PointerEvent): void => {
    this.tooltipController.closeIfOutsidePointerTarget(event.target);
    this.syncActiveTooltipReference();
  };

  constructor(options: HovSkillViewOptions) {
    const messages = getMessages();
    this.onAcquireRequest = options.onAcquireRequest;
    this.onHoldDurationChange = options.onHoldDurationChange;
    this.holdMs = options.holdDurationMs;

    this.root = document.createElement("div");
    this.root.className = "hover-idle-skill-panel";
    this.root.setAttribute("role", "region");
    this.root.setAttribute("aria-label", messages.hovSkill.regionAria);
    this.root.hidden = true;

    const settingsLayer = document.createElement("div");
    settingsLayer.className =
      "hover-idle-game-surface-settings hover-idle-skill-settings";
    settingsLayer.append(this.createHoldDurationControl());

    const scrollContent = document.createElement("div");
    scrollContent.className =
      "hover-idle-game-surface-scroll hover-idle-skill-scroll";
    this.treeLayout = computeHovSkillTreeLayout(HOV_SKILL_DEFINITIONS);

    this.tree = document.createElement("div");
    this.tree.className = "hov-skill-tree";
    this.tree.style.width = `${this.treeLayout.width}px`;
    this.tree.style.height = `${this.treeLayout.height}px`;

    this.svg = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "svg",
    ) as SVGSVGElement;
    this.svg.classList.add("hov-skill-tree-lines");
    this.svg.setAttribute(
      "viewBox",
      `0 0 ${this.treeLayout.width} ${this.treeLayout.height}`,
    );
    this.svg.setAttribute("aria-hidden", "true");
    this.tree.append(this.svg);

    this.drawPrerequisiteLines();

    for (const def of HOV_SKILL_DEFINITIONS) {
      this.createIcon(def);
    }

    scrollContent.append(this.tree);
    this.root.append(settingsLayer, scrollContent);
    options.container.append(this.root);

    this.root.addEventListener("pointerdown", this.handleRootPointerDown);
    this.cleanups.push(() => {
      this.root.removeEventListener("pointerdown", this.handleRootPointerDown);
    });
  }

  setVisible(visible: boolean): void {
    this.root.hidden = !visible;
    if (!visible) {
      this.closeTooltip();
      for (const nodes of this.iconNodes.values()) {
        this.cancelHoldFill(nodes);
      }
    }
  }

  update(params: {
    readonly resources: HovSkillResourceBalances;
    readonly snapshot: HovSkillSnapshot;
    readonly holdDurationMs: HovSkillHoldMs;
    readonly amplifierGainOHovMultiplier: HugeNumber;
    readonly ohovHovCoreGainMultiplier: HugeNumber;
    readonly ohovHovCoreGainScalingUnlocked: boolean;
    readonly mpCapacityCoreOHovHovCoreGainMultiplier?: HugeNumber;
    readonly freeMpCoreOHovHovCoreGainMultiplier?: HugeNumber;
    readonly raBambooMultiplier: HugeNumber;
    readonly raPineMultiplier: HugeNumber;
    readonly apDeerExponentBonus: number;
    readonly apButterflyExponentBonus: number;
    readonly showLockedContent?: boolean;
  }): void {
    this.lastResources = params.resources;
    this.lastSnapshot = params.snapshot;
    this.lastAmplifierGainOHovMultiplier = params.amplifierGainOHovMultiplier;
    this.lastOHovHovCoreGainMultiplier = params.ohovHovCoreGainMultiplier;
    this.lastOHovHovCoreGainScalingUnlocked =
      params.ohovHovCoreGainScalingUnlocked;
    this.lastMpCapacityCoreOHovHovCoreGainMultiplier =
      params.mpCapacityCoreOHovHovCoreGainMultiplier ?? hugeOne();
    this.lastFreeMpCoreOHovHovCoreGainMultiplier =
      params.freeMpCoreOHovHovCoreGainMultiplier ?? hugeOne();
    this.lastRaBambooMultiplier = params.raBambooMultiplier;
    this.lastRaPineMultiplier = params.raPineMultiplier;
    this.lastApDeerExponentBonus = params.apDeerExponentBonus;
    this.lastApButterflyExponentBonus = params.apButterflyExponentBonus;
    this.showLockedContent = params.showLockedContent === true;
    this.syncHoldMs(params.holdDurationMs);
    for (const nodes of this.iconNodes.values()) {
      this.refreshIconState(nodes);
    }
  }

  dispose(): void {
    this.tooltipController.dispose();
    this.clearActiveTooltipReference();
    for (const nodes of this.iconNodes.values()) {
      this.cancelHoldFill(nodes);
      nodes.chargeController.dispose();
    }
    for (const cleanup of this.cleanups) cleanup();
    this.cleanups.length = 0;
    this.iconNodes.clear();
    this.root.remove();
  }

  private createHoldDurationControl(): HTMLDivElement {
    const messages = getMessages();
    const wrapper = document.createElement("div");
    wrapper.className =
      "hover-idle-game-surface-segmented-control hov-skill-hold-duration";
    wrapper.setAttribute("role", "radiogroup");
    wrapper.setAttribute("aria-label", messages.hovSkill.holdDurationLabel);

    const label = document.createElement("span");
    label.className =
      "hover-idle-game-surface-segmented-control-label hov-skill-hold-duration-label";
    label.textContent = `${messages.hovSkill.holdDurationLabel}:`;
    wrapper.append(label);

    const segments = document.createElement("div");
    segments.className =
      "hover-idle-game-surface-segmented-control-options hov-skill-hold-duration-segments";

    for (const optionMs of HOV_SKILL_HOLD_MS_OPTIONS) {
      const button = document.createElement("button");
      button.type = "button";
      button.className =
        "hover-idle-game-surface-segmented-control-option hov-skill-hold-duration-segment";
      button.dataset.virtualPointer = "allow";
      button.setAttribute("role", "radio");
      button.textContent = messages.hovSkill.holdDurationOption(
        String(optionMs / 1000),
      );
      button.dataset.ms = String(optionMs);
      const handler = (): void => {
        this.onHoldDurationChange(optionMs);
      };
      button.addEventListener("pointerenter", handler);
      this.cleanups.push(() => {
        button.removeEventListener("pointerenter", handler);
      });
      this.holdDurationButtons.push(button);
      segments.append(button);
    }
    wrapper.append(segments);
    this.syncHoldDurationControl();
    return wrapper;
  }

  private syncHoldMs(ms: HovSkillHoldMs): void {
    if (this.holdMs === ms) return;
    this.holdMs = ms;
    for (const nodes of this.iconNodes.values()) {
      this.cancelHoldFill(nodes);
    }
    this.syncHoldDurationControl();
  }

  private syncHoldDurationControl(): void {
    for (const button of this.holdDurationButtons) {
      const active = Number(button.dataset.ms) === this.holdMs;
      button.dataset.active = active ? "true" : "false";
      button.setAttribute("aria-checked", active ? "true" : "false");
    }
  }

  private createIcon(definition: HovSkillDefinition): void {
    const messages = getMessages();
    const layout = this.treeLayout.nodes.get(definition.id);
    if (!layout) {
      throw new Error(`Missing hov skill layout for ${definition.id}`);
    }

    const button = document.createElement("button");
    button.type = "button";
    button.className = "hover-idle-charge-up-icon";
    button.dataset.skillId = definition.id;
    button.dataset.skillRowIndex = String(definition.row);
    button.dataset.skillColumnIndex = String(definition.column);
    button.dataset.virtualPointer = "allow";
    button.style.left = `${layout.left}px`;
    button.style.top = `${layout.top}px`;

    const clip = document.createElement("div");
    clip.className = "hover-idle-charge-up-icon-clip";

    const fill = document.createElement("div");
    fill.className = "hover-idle-charge-up-icon-fill";

    const label = document.createElement("span");
    label.className = "hover-idle-charge-up-icon-label";
    label.textContent = messages.hovSkill.shortLabels[definition.id];

    clip.append(fill, label);

    const coordinateLabel = document.createElement("span");
    coordinateLabel.className = "hov-skill-icon-coordinate";
    coordinateLabel.setAttribute("aria-hidden", "true");
    coordinateLabel.textContent = formatHovSkillCoordinate(
      definition.row,
      definition.column,
    );

    const externalBadge = document.createElement("span");
    externalBadge.className = "hov-skill-icon-external";
    externalBadge.setAttribute("aria-hidden", "true");
    externalBadge.hidden = true;

    const tooltip = document.createElement("div");
    tooltip.className = "hover-idle-charge-up-icon-tooltip";
    tooltip.setAttribute("role", "tooltip");
    tooltip.setAttribute("aria-hidden", "true");
    tooltip.hidden = true;

    const spec = buildHovSkillTooltipSpec(
      definition.id,
      messages.hovSkill,
      this.getCurrentTooltipContext(),
    );

    const tooltipName = document.createElement("div");
    tooltipName.className = "hover-idle-charge-up-icon-tooltip-name";
    tooltipName.textContent = spec.title;

    const tooltipDescription = document.createElement("div");
    tooltipDescription.className =
      "hover-idle-charge-up-icon-tooltip-description";
    tooltipDescription.textContent = spec.description;

    let tooltipInfluence: HTMLDivElement | null = null;
    if (spec.influence !== null) {
      tooltipInfluence = document.createElement("div");
      tooltipInfluence.className =
        "hover-idle-charge-up-icon-tooltip-influence";
      tooltipInfluence.textContent = `${messages.hovSkill.influenceLabel}: ${formatHovSkillInfluenceText(spec.influence)}`;
    }

    const tooltipCost = document.createElement("div");
    tooltipCost.className = "hover-idle-charge-up-icon-tooltip-cost";
    tooltipCost.textContent = `${messages.hovSkill.costLabel}: ${formatHovSkillCost(definition.cost)}`;

    const tooltipState = document.createElement("div");
    tooltipState.className = "hover-idle-charge-up-icon-tooltip-state";

    tooltip.append(tooltipName, tooltipDescription);
    if (tooltipInfluence !== null) {
      tooltip.append(tooltipInfluence);
    }
    tooltip.append(tooltipCost, tooltipState);
    button.append(clip, coordinateLabel, externalBadge, tooltip);
    this.tree.append(button);

    const nodesBase = {
      definition,
      button,
      fill,
      label,
      tooltip,
      tooltipName,
      tooltipDescription,
      tooltipInfluence,
      tooltipCost,
      tooltipState,
      externalBadge,
    };
    const chargeController = new HoverChargeExecutionController({
      getDurationMs: () => this.holdMs,
      canExecute: () => this.canAcquireSkillDefinition(definition),
      onProgress: (progress) => this.applyHoldFillProgress(fill, progress),
      onExecute: () => this.acquireSkillDefinition(definition, fill),
    });
    const nodes: IconNodes = {
      ...nodesBase,
      chargeController,
    };
    this.iconNodes.set(definition.id, nodes);

    const handleEnter = (): void => this.beginHoldFill(nodes);
    const handleLeave = (): void => this.releaseHoldFill(nodes);
    const handleTooltipPointerEnter = (event: PointerEvent): void => {
      if (event.pointerType === "touch") return;
      this.openTooltip(nodes, "sticky");
    };
    const handleTooltipPointerDown = (event: PointerEvent): void => {
      if (event.pointerType !== "touch") return;
      this.openTooltip(nodes, "sticky");
    };
    const handleTooltipFocus = (): void => {
      this.openTooltip(nodes, "focus");
    };
    const handleTooltipBlur = (): void => {
      this.tooltipController.removeSource(definition.id, "focus");
      this.syncActiveTooltipReference();
    };

    button.addEventListener("pointerenter", handleEnter);
    button.addEventListener("focus", handleEnter);
    button.addEventListener("pointerleave", handleLeave);
    button.addEventListener("blur", handleLeave);
    button.addEventListener("pointerenter", handleTooltipPointerEnter);
    button.addEventListener("pointerdown", handleTooltipPointerDown);
    button.addEventListener("focus", handleTooltipFocus);
    button.addEventListener("blur", handleTooltipBlur);
    this.cleanups.push(() => {
      button.removeEventListener("pointerenter", handleEnter);
      button.removeEventListener("focus", handleEnter);
      button.removeEventListener("pointerleave", handleLeave);
      button.removeEventListener("blur", handleLeave);
      button.removeEventListener("pointerenter", handleTooltipPointerEnter);
      button.removeEventListener("pointerdown", handleTooltipPointerDown);
      button.removeEventListener("focus", handleTooltipFocus);
      button.removeEventListener("blur", handleTooltipBlur);
    });
  }

  private openTooltip(nodes: IconNodes, source: StickyTooltipSource): void {
    if (!this.isSkillContentVisible(nodes.definition)) {
      this.closeTooltip();
      return;
    }
    this.setActiveTooltipReference(nodes.button);
    this.tooltipController.open({
      id: nodes.definition.id,
      reference: nodes.button,
      tooltip: nodes.tooltip,
      source,
      floatingOptions: HOV_SKILL_TOOLTIP_FLOATING_OPTIONS,
    });
  }

  private closeTooltip(): void {
    this.tooltipController.close();
    this.clearActiveTooltipReference();
  }

  private syncActiveTooltipReference(): void {
    const active = this.tooltipController.getActiveSnapshot();
    if (active === null) {
      this.clearActiveTooltipReference();
      return;
    }

    const activeNodes = this.iconNodes.get(active.id as HovSkillId);
    if (activeNodes === undefined) {
      this.clearActiveTooltipReference();
      return;
    }

    this.setActiveTooltipReference(activeNodes.button);
  }

  private setActiveTooltipReference(button: HTMLButtonElement): void {
    if (this.activeTooltipReference === button) return;
    this.clearActiveTooltipReference();
    this.activeTooltipReference = button;
    button.dataset[HOV_SKILL_TOOLTIP_ACTIVE_DATASET_KEY] = "true";
  }

  private clearActiveTooltipReference(): void {
    if (this.activeTooltipReference === null) return;
    delete this.activeTooltipReference.dataset[
      HOV_SKILL_TOOLTIP_ACTIVE_DATASET_KEY
    ];
    this.activeTooltipReference = null;
  }

  private drawPrerequisiteLines(): void {
    for (const layout of this.treeLayout.lines) {
      const line = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "line",
      );
      line.setAttribute("x1", String(layout.x1));
      line.setAttribute("y1", String(layout.y1));
      line.setAttribute("x2", String(layout.x2));
      line.setAttribute("y2", String(layout.y2));
      line.setAttribute("class", "hov-skill-tree-line");
      line.dataset.from = layout.from;
      line.dataset.to = layout.to;
      this.svg.append(line);
    }
  }

  private refreshIconState(nodes: IconNodes): void {
    const messages = getMessages();
    const acquired = this.lastSnapshot.acquired.has(nodes.definition.id);
    const prereqMet = this.isSkillContentUnlocked(nodes.definition);
    const contentVisible = prereqMet || this.showLockedContent;
    const affordable = canAffordHovSkillCost(
      nodes.definition.cost,
      this.lastResources,
    );
    const ready = !acquired && prereqMet && affordable;

    nodes.button.dataset.acquired = acquired ? "true" : "false";
    nodes.button.dataset.prereqMet = prereqMet ? "true" : "false";
    nodes.button.dataset.contentUnlocked = prereqMet ? "true" : "false";
    nodes.button.dataset.contentConcealed = contentVisible ? "false" : "true";
    nodes.button.dataset.affordable = affordable ? "true" : "false";
    nodes.button.dataset.ready = ready ? "true" : "false";
    nodes.button.disabled = acquired;
    nodes.label.textContent = contentVisible
      ? messages.hovSkill.shortLabels[nodes.definition.id]
      : "?";

    let stateText: string;
    if (acquired) {
      stateText = messages.hovSkill.stateAcquired;
    } else if (!prereqMet) {
      stateText = messages.hovSkill.stateMissingPrerequisites;
    } else if (!affordable) {
      stateText = messages.hovSkill.stateMissingResource(
        formatForContext(
          getHovSkillCostShortfall(nodes.definition.cost, this.lastResources),
          "tooltip",
        ),
        getHovSkillCostResourceLabel(nodes.definition.cost.resource),
      );
    } else {
      stateText = messages.hovSkill.stateHoldToAcquire(
        String(this.holdMs / 1000),
      );
    }
    nodes.tooltipState.textContent = stateText;
    nodes.tooltipState.dataset.acquired = acquired ? "true" : "false";
    nodes.tooltipState.dataset.ready = ready ? "true" : "false";
    nodes.tooltipState.hidden = acquired;
    nodes.tooltipCost.hidden = acquired;

    if (acquired || !ready) {
      this.cancelHoldFill(nodes);
    }

    if (
      !contentVisible &&
      this.tooltipController.getActiveSnapshot()?.id === nodes.definition.id
    ) {
      this.closeTooltip();
    }

    this.refreshExternalBadge(nodes, contentVisible);
    this.refreshTooltipSpec(nodes);
    this.refreshLineState(nodes.definition.id, acquired);
  }

  private refreshTooltipSpec(nodes: IconNodes): void {
    const messages = getMessages();
    const spec: HovSkillTooltipSpec = buildHovSkillTooltipSpec(
      nodes.definition.id,
      messages.hovSkill,
      this.getCurrentTooltipContext(),
    );
    nodes.tooltipName.textContent = spec.title;
    nodes.tooltipDescription.textContent = spec.description;
    if (nodes.tooltipInfluence !== null && spec.influence !== null) {
      nodes.tooltipInfluence.textContent = `${messages.hovSkill.influenceLabel}: ${formatHovSkillInfluenceText(spec.influence)}`;
    }
  }

  private getCurrentTooltipContext(): HovSkillTooltipContext {
    return {
      amplifierGainOHovMultiplier: this.lastAmplifierGainOHovMultiplier,
      ohovHovCoreGainMultiplier: this.lastOHovHovCoreGainMultiplier,
      ohovHovCoreGainScalingUnlocked: this.lastOHovHovCoreGainScalingUnlocked,
      mpCapacityCoreOHovHovCoreGainMultiplier:
        this.lastMpCapacityCoreOHovHovCoreGainMultiplier,
      freeMpCoreOHovHovCoreGainMultiplier:
        this.lastFreeMpCoreOHovHovCoreGainMultiplier,
      raBambooMultiplier: this.lastRaBambooMultiplier,
      raPineMultiplier: this.lastRaPineMultiplier,
      apDeerExponentBonus: this.lastApDeerExponentBonus,
      apButterflyExponentBonus: this.lastApButterflyExponentBonus,
    };
  }

  private refreshExternalBadge(
    nodes: IconNodes,
    contentUnlocked: boolean,
  ): void {
    const messages = getMessages();
    const entry = this.findSnapshotEntry(nodes.definition.id);
    const multiplier = entry?.externalMultiplier;
    if (
      !contentUnlocked ||
      multiplier === undefined ||
      !isHovSkillExternalMultiplierActive(multiplier)
    ) {
      nodes.externalBadge.hidden = true;
      nodes.externalBadge.textContent = "";
      return;
    }
    nodes.externalBadge.hidden = false;
    nodes.externalBadge.textContent = `${messages.hovSkill.externalLabel} ${formatHovSkillExternalMultiplier(multiplier)}`;
  }

  private findSnapshotEntry(id: HovSkillId): HovSkillSnapshotEntry | undefined {
    for (const entry of this.lastSnapshot.entries) {
      if (entry.id === id) return entry;
    }
    return undefined;
  }

  private refreshLineState(id: HovSkillId, acquired: boolean): void {
    const lines = this.svg.querySelectorAll<SVGLineElement>(
      `line[data-from="${id}"]`,
    );
    for (const line of lines) {
      if (acquired) {
        line.dataset.active = "true";
      } else {
        delete line.dataset.active;
      }
    }
  }

  private beginHoldFill(nodes: IconNodes): void {
    if (!this.canAcquireSkill(nodes)) return;
    nodes.chargeController.setHovered(true);
  }

  private releaseHoldFill(nodes: IconNodes): void {
    nodes.chargeController.setHovered(false);
    if (nodes.chargeController.getProgress() === 0) {
      this.resetFill(nodes);
    }
  }

  private cancelHoldFill(nodes: IconNodes): void {
    nodes.chargeController.cancel();
    this.resetFill(nodes);
  }

  private resetFill(nodes: IconNodes): void {
    this.resetFillElement(nodes.fill);
  }

  private canAcquireSkill(nodes: IconNodes): boolean {
    return this.canAcquireSkillDefinition(nodes.definition);
  }

  private canAcquireSkillDefinition(definition: HovSkillDefinition): boolean {
    const acquired = this.lastSnapshot.acquired.has(definition.id);
    if (acquired) return false;
    const prereqMet = definition.prerequisites.every((req) =>
      this.lastSnapshot.acquired.has(req),
    );
    if (!prereqMet) return false;
    return canAffordHovSkillCost(definition.cost, this.lastResources);
  }

  private isSkillContentUnlocked(definition: HovSkillDefinition): boolean {
    return definition.prerequisites.every((prerequisiteId) =>
      this.lastSnapshot.acquired.has(prerequisiteId),
    );
  }

  private isSkillContentVisible(definition: HovSkillDefinition): boolean {
    return this.showLockedContent || this.isSkillContentUnlocked(definition);
  }

  private applyHoldFillProgress(fill: HTMLDivElement, progress: number): void {
    fill.style.transition = "height 80ms linear";
    fill.style.height = `${progress * 100}%`;
  }

  private acquireSkillDefinition(
    definition: HovSkillDefinition,
    fill: HTMLDivElement,
  ): void {
    const succeeded = this.onAcquireRequest(definition.id);
    if (!succeeded) {
      this.resetFillElement(fill);
    }
  }

  private resetFillElement(fill: HTMLDivElement): void {
    fill.style.transition = "height 200ms ease-out";
    fill.style.height = "0%";
  }
}

function formatHovSkillCost(cost: HovSkillCost): string {
  return `${formatForContext(
    cost.amount,
    "tooltip",
  )} ${getHovSkillCostResourceLabel(cost.resource)}`;
}

function getHovSkillCostResourceLabel(
  resource: HovSkillCost["resource"],
): string {
  return getMessages().hovSkill.costResources[resource];
}
